import { motion } from "framer-motion";
import { SmartImage } from "./SmartImage";
import { imgSrc, imgStyle } from "@/lib/img";

const Section = ({ children, className = "" }) => (
  <div className={`max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 ${className}`}>{children}</div>
);

export const SectionHeading = ({ script, title, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
    transition={{ duration: 0.7 }}
    className="text-center mb-12 sm:mb-16"
    data-testid={testid}
  >
    {script && <p className="font-script text-2xl sm:text-3xl text-[var(--gold)]">{script}</p>}
    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal text-[var(--burgundy)] mt-1">
      {title}
    </h2>
    <div className="gold-rule w-24 mx-auto mt-5" />
  </motion.div>
);

export const Story = ({ story }) => {
  const isTimeline = story?.mode === "timeline" && (story?.timeline || []).some((t) => t.title || t.text || t.image);
  const items = (story?.timeline || []).filter((t) => t.title || t.text || t.image);
  return (
  <section id="story" className="py-20 sm:py-28 lg:py-32 bg-[var(--ivory)]" data-testid="story-section">
    <Section>
      <SectionHeading script="the beginning" title={story?.title || "Our Story"} testid="story-heading" />
      {isTimeline ? (
        <div className="relative max-w-2xl mx-auto mt-2" data-testid="story-timeline">
          <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-[var(--border-gold)]" />
          {items.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="relative pl-12 pb-10 last:pb-0"
              data-testid={`story-timeline-item-${i}`}
            >
              <span className="absolute left-1 top-1.5 w-4 h-4 rounded-full bg-[var(--gold)] ring-4 ring-[var(--ivory)]" />
              {t.label && <p className="font-script text-xl text-[var(--gold)]">{t.label}</p>}
              {t.title && <h3 className="font-serif text-2xl text-[var(--burgundy)] mt-0.5">{t.title}</h3>}
              {t.image && (
                <div className="rounded-lg w-full h-72 overflow-hidden shadow-md my-3">
                  <img src={imgSrc(t.image)} style={imgStyle(t.image)} alt={t.title || `Moment ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                </div>
              )}
              {t.text && <p className="text-[var(--text-secondary)] leading-relaxed whitespace-pre-line">{t.text}</p>}
            </motion.div>
          ))}
        </div>
      ) : (
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          {story?.image1 && (
            <div className="rounded-lg w-full h-[320px] sm:h-[420px] overflow-hidden shadow-xl" data-testid="story-image-1">
              <img src={imgSrc(story.image1)} style={imgStyle(story.image1)} alt="Couple" loading="lazy" className="w-full h-full object-cover" />
            </div>
          )}
          {story?.image2 && (
            <div className="hidden sm:block absolute -bottom-10 -right-6 w-40 h-52 overflow-hidden rounded-lg shadow-2xl border-4 border-[var(--card)]" data-testid="story-image-2">
              <img src={imgSrc(story.image2)} style={imgStyle(story.image2)} alt="Couple" loading="lazy" className="w-full h-full object-cover" />
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="space-y-6"
        >
          {(story?.paragraphs || []).map((p, i) => (
            <p key={i} className="text-[var(--text-secondary)] text-base sm:text-lg leading-relaxed" data-testid={`story-paragraph-${i}`}>
              {p}
            </p>
          ))}
        </motion.div>
      </div>
      )}

      {story?.photos?.length > 0 && (
        <div className="columns-2 md:columns-3 gap-4 sm:gap-5 mt-16 max-w-5xl mx-auto [column-fill:balance]" data-testid="story-photos">
          {story.photos.filter((p) => p.url).map((p, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              className="mb-4 sm:mb-5 break-inside-avoid group"
            >
              <div className="overflow-hidden rounded-2xl transition-transform duration-500 group-hover:-translate-y-1 group-hover:shadow-xl">
                <SmartImage src={p.url} aspect={p.aspect} fit={p.fit} focal={p.focal} position={p.position} zoom={p.zoom} alt={`Story photo ${i + 1}`} className="group-hover:scale-[1.03] transition-transform duration-500" testid={`story-photo-${i}`} />
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </Section>
  </section>
  );
};
